export type DisplayTemplateType = 'Spotlight' | 'Banner' | 'Ticker' | 'Split';
export type DisplayOrientation = 'Landscape' | 'Portrait';

export interface SpotLightMedia {
  name: string;
  mediaId: string;
  mimeType: string;
  orientation?: DisplayOrientation | null;
  url?: string;
  mediaUrl?: string;
  src?: string;
  showTime: { weekday: string; end_time: string; start_time: string }[];
  sequenceNo: string;
  displayTime: string;
}

export interface DisplayAttribute {
  pin?: string | null;
  /** 'S' = single media, 'P' = playlist. Absent on locally-built drafts. */
  mode?: 'S' | 'P' | null;
  playlistId?: string | null;
  orientation?: DisplayOrientation | null;
  heartbeatInterval?: number | null;
  timeSlots?: { weekday: string; start_time: string; end_time: string }[];
  spotLightMedia: SpotLightMedia[];
  fallbackMedia?: SpotLightMedia | null;
}

export interface DisplayItem {
  id: string;
  displayId?: string;
  templateId?: string;
  locationId: string;
  pin?: string | null;
  secretKey?: string;
  displayName?: string;
  name?: string;
  templateType: string;
  isDeleted?: boolean;
  attribute: DisplayAttribute;
  createdAt?: string;
  updatedAt?: string;
}

// ── Media Library ────────────────────────────────────────────────────────────
export type MediaKind = 'image' | 'video';

export interface MediaLibraryItem {
  id: string;
  name: string;
  mimeType: string;
  kind: MediaKind;
  url: string;
  sizeBytes: number;
  /** Tagged at upload time — determines which sections/playlists this media is eligible for. */
  orientation: DisplayOrientation;
  createdAt: string;
  updatedAt: string;
}

/** Max media items a location's library can hold — mock cap until backend defines this. */
export const MEDIA_LIBRARY_SLOT_LIMIT = 35;

/** Max upload size per file — matches the /display/upload-media backend limit. */
export const MEDIA_MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

/** Extension → mime fallback for picked files whose `type` is blank (common with Android
 *  content providers such as Google Photos / Drive / some file managers). */
export const MEDIA_MIME_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp',
  bmp: 'image/bmp', avif: 'image/avif', heic: 'image/heic', heif: 'image/heif',
  mp4: 'video/mp4', m4v: 'video/x-m4v', mov: 'video/quicktime', webm: 'video/webm',
  mkv: 'video/x-matroska', '3gp': 'video/3gpp', avi: 'video/x-msvideo',
};

// ── Scheduling ────────────────────────────────────────────────────────────────
export interface ScheduleSlot {
  weekday: string;
  start_time: string;
  end_time: string;
}

export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

// ── Playlists ────────────────────────────────────────────────────────────────
/** Playlists are a schedule-free, reusable media grouping — weekly time slots are configured
 *  per media at Create Display time instead, so the same playlist can run different schedules
 *  on different displays. */
export interface PlaylistMediaEntry {
  mediaId: string;
  sequenceNo: number;
  displayTime: string;
}

export interface Playlist {
  id: string;
  name: string;
  orientation: DisplayOrientation;
  mediaEntries: PlaylistMediaEntry[];
  createdAt: string;
  updatedAt: string;
}

// ── Create Display wizard (client-side draft shape) ───────────────────────────
export type ContentAssignmentType = 'single' | 'playlist';
